/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from '@scuf/localization';
import './UtilityServiceManageBillTheme.scss';
import { DataContextProvider } from '@forge/data-connector';
import IEnergyManagementProps, {
  IAny,
  ISiteCard
} from '../DashboardKPIWidgets/IEnergyManagementProps';
import descriptors from '../../locales/en-US/descriptors';
import { ApiContext } from '../../context/ApiContext';
import useConnectorAndDynamicProps from '../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import UtilityBillUploadBody from './UtilityBillUploadBody';
import useMounted from '../shared/useMount/useMount';
import { Icon } from '@scuf/common';
import { modifyResponseFromAzure } from './UtilityManageBillUtils';
import moment from 'moment';
import { MULTIPLE_UPLOAD_FILES } from '../shared/Utils/Constants';

const UtilityServiceManageBillComponent = (props: IAny) => {
  const { bearerToken, requestContext, emaasContext, utilityCellData } = props;
  const d = descriptors.UtilityBuilder;
  const [t] = useTranslation();
  const apiContext = useContext(ApiContext);
  const isMounted = useMounted();
  const connectorConfig = {
    name: 'manage-utility-Bill'
  };

  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });
  const { all, loading, error, status } = dataConnector.data;
  const monthYearData = (all as unknown) as IAny;
  const isDataAvailable = monthYearData ? true : false;
  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState(([] as unknown) as IAny);
  const [isNewBill, setIsNewBill] = useState(false);

  const [billDetails, setBillDetails] = useState<IAny>({});
  const [newBillClick, setNewBillClick] = useState<IAny>('');
  const [billClickLoader, setBillClickLoader] = useState<boolean>(false);
  const [isPDFExtracted, setIsPDFExtracted] = useState<boolean>(false);

  const onRefresh = () => {
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();
  };

  const onMultipleupload = () => {
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();
  };

  useEffect(() => {
    if (isMounted && utilityCellData.logicalAccountId) {
      onRefresh();
    }
  }, [requestContext]);

  const onBillSave = () => {
    onRefresh();
    return true;
  };

  useEffect(() => {
    setIsLoading(loading);
    if (
      (monthYearData && monthYearData.length && monthYearData.length === 0) ||
      status === 204
    ) {
      setData([]);
      utilityCellData &&
        utilityCellData.integrationType === 'Manual' &&
        setIsNewBill(true);
    }
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setData(monthYearData.data);
        if (
          monthYearData &&
          monthYearData.data &&
          monthYearData.data.length &&
          monthYearData.data.length === MULTIPLE_UPLOAD_FILES
        ) {
          setIsMultipleUploaded(false);
        }
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);

  const onBillClick = async (payload: IAny) => {
    // setIsMultipleUploaded(false);
    setBillClickLoader(true);
    const billDetails = await apiContext.getBillDetails(payload);
    setBillClickLoader(false);
    setBillDetails(billDetails.data);
    setIsNewBill(false);
  };

  const [refreshTable, setRefreshTable] = useState(false);
  const [isMultipleUploaded, setIsMultipleUploaded] = useState(false);
  const [timer, setTimer] = useState<IAny>('');

  useEffect(() => {
    if (isMultipleUploaded) {
      setTimer(
        setInterval(() => {
          onMultipleupload();
        }, 5000)
      );
    } else {
      clearInterval(timer);
    }
  }, [isMultipleUploaded]);

  useEffect(() => {
    if (isNewBill) {
      setTimeout(() => {
        setRefreshTable(false);
      }, 1000);
    }
  }, [isNewBill]);

  const addNewBill = () => {
    // setBillClickLoader(true);
    //setIsNewBill(true);
    setRefreshTable(true);

    setTimeout(() => {
      setIsNewBill(true);
    }, 2000);
    setIsMultipleUploaded(false);
  };

  const handlePDFData = async (res: IAny, uploadedFile: IAny) => {
    res.data.uploadedBill = uploadedFile;
    setIsPDFExtracted(true);
    setBillDetails(
      modifyResponseFromAzure(JSON.parse(JSON.stringify(res.data)))
    );
    setIsNewBill(false);
  };

  const [isWrongFile, setIsWrongFile] = useState<boolean>(false);

  return (
    <div className="utility-service-manage-bill-container">
      <div className="manage-bill-header-container">
        <div className="account-number-txt">
          <span className="header">{t(d.ACCOUNTNUMBER)}</span>
          <span className="subheader">
            {utilityCellData && utilityCellData.accountNumber}
          </span>
        </div>
        <div className="account-number-details">
          <span className="header">{t(d.UTILITYTYPE)}</span>
          <span className="subheader">
            {utilityCellData && utilityCellData.utilityType}
          </span>
        </div>
      </div>
      {utilityCellData && (
        <div
          style={
            isNewBill || isPDFExtracted || isMultipleUploaded
              ? { pointerEvents: 'none' }
              : {}
          }
          className="Add-Electricity-Bill"
          onClick={addNewBill}
        >
          <Icon
            className="Add-Electricity-Bill-icon"
            root="common"
            name="badge-plus"
            size="small"
          />
          {/* {utilityCellData.utilityType === 'Electricity'
            ? t(d.ADDELECTRICITYBILL)
            : t(d.ADDGASBILL)} */}
          {t(d.ADDGASBILL, { utilityType: utilityCellData.utilityType })}
          {/* Add Electricity Bill */}
        </div>
      )}
      <UtilityBillUploadBody
        isNewBill={isNewBill}
        billDetails={billDetails}
        onBillClick={onBillClick}
        monthYearData={data}
        utilityCellData={utilityCellData}
        onBillSave={onBillSave}
        setIsNewBill={setIsNewBill}
        newBillClick={newBillClick}
        billClickLoader={billClickLoader}
        setPDFResponse={handlePDFData}
        refreshTable={refreshTable}
        isPDFExtracted={isPDFExtracted}
        setIsPDFExtracted={setIsPDFExtracted}
        addNewBill={addNewBill}
        setRefreshTable={setRefreshTable}
        isMultipleUploaded={isMultipleUploaded}
        setIsMultipleUploaded={setIsMultipleUploaded}
        setIsWrongFile={setIsWrongFile}
        isWrongFile={isWrongFile}
      />
    </div>
  );
};

export default UtilityServiceManageBillComponent;
